import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, trackEvent } from "../api";

// Institution-level outreach attribution: campaign CODE only (validated
// server-side), never a personal identifier. ?ref=uwm-pilot-01 or
// ?utm_campaign=...&utm_content=uwm both map to the same code.
function captureEduRef() {
  try {
    const q = new URLSearchParams(window.location.search);
    const raw = (q.get("ref") || q.get("utm_content") || "").toLowerCase();
    if (!/^[a-z0-9-]{1,32}$/.test(raw)) return null;
    sessionStorage.setItem("pg_ref", raw);
    return raw;
  } catch { return null; }
}

function currentEduRef() {
  try { return sessionStorage.getItem("pg_ref") || undefined; } catch { return undefined; }
}

export default function EduProgramPage() {
  const [program, setProgram] = useState(null);
  const [directory, setDirectory] = useState([]);

  useEffect(() => {
    api.get("/edu/program").then((r) => setProgram(r.data)).catch(() => {});
    api.get("/edu/directory").then((r) => setDirectory(r.data.results || [])).catch(() => {});
    try {
      const ref = captureEduRef();
      if (ref && !sessionStorage.getItem(`pg_edu_v_${ref}`)) {
        sessionStorage.setItem(`pg_edu_v_${ref}`, "1");
        trackEvent("edu_visit", { ref });
      }
    } catch { /* private mode */ }
  }, []);

  const trackCta = () => trackEvent("edu_cta_click", { ref: currentEduRef() });

  if (!program) return <div className="p-20 text-center text-slate-500" data-testid="edu-loading">Ładowanie…</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="edu-program-page">
      <Helmet><title>PlanGmina EDU — PlanGmina dla polskich uczelni</title></Helmet>

      <p className="text-xs font-semibold uppercase tracking-widest text-red-700">{program.name}</p>
      <h1 className="mt-2 text-4xl font-bold text-slate-900">{program.subtitle_pl}</h1>
      <p className="mt-4 whitespace-pre-line text-base text-slate-700">{program.message_pl}</p>
      <p className="mt-2 text-sm text-slate-500">{program.secondary_pl}</p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/edu/zgloszenie" data-testid="edu-apply-cta" onClick={trackCta}
          className="rounded-lg bg-red-700 px-5 py-3 text-sm font-semibold text-white hover:bg-red-800 transition-colors">
          {program.cta_pl}
        </Link>
        <Link to="/edu/panel" data-testid="edu-panel-link"
          className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:border-slate-400 transition-colors">
          Panel EDU
        </Link>
      </div>

      <section className="mt-10" data-testid="edu-who">
        <h2 className="text-lg font-bold text-slate-900">Dla kogo</h2>
        <ul className="mt-3 space-y-1 text-sm text-slate-700">
          <li>• Studentki i studenci — ćwiczenia, projekty, prace dyplomowe</li>
          <li>• Prowadzący — zajęcia, studia przypadków, nadzór nad projektami</li>
          <li>• Naukowcy — badania z pełną proweniencją źródeł</li>
          <li>• Uczelnie publiczne, techniczne, rolnicze, ekonomiczne i niepubliczne</li>
        </ul>
      </section>

      <section className="mt-10" data-testid="edu-research-areas">
        <h2 className="text-lg font-bold text-slate-900">Obszary badawcze</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {program.research_areas_pl.map((a) => (
            <span key={a} className="rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700">{a}</span>
          ))}
        </div>
      </section>

      <section className="mt-10" data-testid="edu-how">
        <h2 className="text-lg font-bold text-slate-900">Jak dołączyć</h2>
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate-700">
          <li>Uczelnia składa zgłoszenie (2 minuty).</li>
          <li>Weryfikujemy domenę uczelni i kontakt wydziałowy.</li>
          <li>Po zatwierdzeniu administrator uczelni zaprasza prowadzących i studentów.</li>
          <li>Praca odbywa się w projektach dydaktycznych i badawczych.</li>
        </ol>
      </section>

      <section className="mt-10 rounded-xl border border-slate-900 p-5" data-testid="edu-pilot">
        <h2 className="text-lg font-bold text-slate-900">Program pilotażowy</h2>
        <p className="mt-2 text-sm text-slate-700">{program.pilot_pl}</p>
      </section>

      <section className="mt-6 rounded-xl border-2 border-red-700 bg-red-50 p-5" data-testid="edu-terms">
        <h2 className="text-lg font-bold text-red-900">Warunki niekomercyjne</h2>
        <p className="mt-2 text-sm text-red-900">{program.terms_pl}</p>
        <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-red-800">{program.watermark_pl}</p>
        <p className="mt-1 text-xs text-red-700">{program.watermark_en}</p>
        <p className="mt-3 text-xs text-slate-600">
          Każdy raport akademicki zawiera trwały znak wodny, identyfikator raportu,
          uczelnię, projekt i pełną proweniencję źródeł. Raporty profesjonalne
          (due diligence) wymagają odrębnej licencji komercyjnej.
        </p>
      </section>

      <section className="mt-6" data-testid="edu-legal-links">
        <h2 className="text-sm font-bold uppercase text-slate-500">Dokumenty programu</h2>
        <p className="mt-2 text-sm text-slate-700">
          <Link to="/edu/regulamin" className="text-red-700 underline" data-testid="edu-link-regulamin">Regulamin PlanGmina EDU</Link>
          {" · "}
          <Link to="/edu/prywatnosc" className="text-red-700 underline" data-testid="edu-link-prywatnosc">Informacja o przetwarzaniu danych osobowych</Link>
          {" · "}
          <Link to="/edu/retencja" className="text-red-700 underline" data-testid="edu-link-retencja">Zasady retencji i usuwania danych</Link>
        </p>
      </section>

      <section className="mt-10" data-testid="edu-directory">
        <h2 className="text-lg font-bold text-slate-900">Uczelnie uczestniczące</h2>
        {directory.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Katalog jest publikowany za zgodą uczelni — pierwsze uczelnie pilotażowe w trakcie weryfikacji.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200">
            {directory.map((i) => (
              <li key={i.name} className="py-3" data-testid={`edu-directory-${i.name}`}>
                <p className="text-sm font-semibold text-slate-900">{i.name} <span className="font-normal text-slate-500">· {i.city}</span></p>
                {i.directory_note && <p className="text-xs text-slate-600">{i.directory_note}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="mt-10 border-t border-slate-200 pt-4 text-xs text-slate-500">
        {program.attribution_pl} · AI przygotowuje. Człowiek decyduje.
      </p>
      <p className="mt-2 text-xs text-slate-500" data-testid="edu-corporate">
        PlanGmina — projekt rozwijany przez Hussar Holdings LLC ·{" "}
        <a href="https://hussarholdings.pl" target="_blank" rel="noopener noreferrer"
          data-testid="edu-corporate-site"
          className="underline underline-offset-2 hover:text-slate-700">hussarholdings.pl</a> ·{" "}
        <a href="mailto:contact@hussarholdings.pl" data-testid="edu-corporate-email"
          className="underline underline-offset-2 hover:text-slate-700">contact@hussarholdings.pl</a>
      </p>
    </div>
  );
}
